import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, PlayCircle, CheckCircle2, Star, Search, GraduationCap } from "lucide-react";
import { Button } from "../ui/button";
import { STATS, BRAND } from "../../mock";
import { CountUp } from "../common";
import api from "../../lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1517486808906-6ca8b3f04846";

function HeroSearch({ onSearch }) {
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [university, setUniversity] = useState("");
  const [filters, setFilters] = useState({ countries: [], universities: [] });
  const [showSug, setShowSug] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => { api.get("/public/course-filters").then((r) => setFilters(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setShowSug(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const allUnis = filters.universities || [];
  const ql = q.trim().toLowerCase();

  const uniGroups = useMemo(() => {
    const list = allUnis
      .filter((u) => !country || (u.countries || []).includes(country))
      .filter((u) => !ql || u.name.toLowerCase().includes(ql) || (u.courses || []).some((c) => c.name.toLowerCase().includes(ql)))
      .sort((a, b) => ((a.countries || [])[0] || "zz").localeCompare((b.countries || [])[0] || "zz") || a.name.localeCompare(b.name));
    const g = {};
    list.forEach((u) => { const c = (u.countries || [])[0] || "Other"; (g[c] = g[c] || []).push(u); });
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [allUnis, country, ql]);

  const suggestions = useMemo(() => {
    const src = allUnis
      .filter((u) => !country || (u.countries || []).includes(country))
      .filter((u) => !university || u.id === university);
    const names = new Set();
    src.forEach((u) => (u.courses || []).forEach((c) => { if (!ql || c.name.toLowerCase().includes(ql)) names.add(c.name); }));
    return [...names].sort().slice(0, 6);
  }, [allUnis, country, university, ql]);

  const go = () => { onSearch({ q: q.trim(), country, level: "", university, nonce: Date.now() }); };

  const selCls = "h-11 rounded-full border border-slate-200 bg-white px-4 text-sm text-slate-700";

  return (
    <div ref={boxRef} className="mt-8 bg-white/95 backdrop-blur rounded-[1.75rem] p-3 shadow-2xl max-w-xl" data-testid="hero-search">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="hero-search-input"
            value={q}
            onChange={(e) => { setQ(e.target.value); setShowSug(true); }}
            onFocus={() => setShowSug(true)}
            onKeyDown={(e) => e.key === "Enter" && go()}
            placeholder="Search course or university…"
            autoComplete="off"
            className="w-full h-11 rounded-full border border-slate-200 bg-white pl-10 pr-4 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#004030]/30"
          />
          {showSug && suggestions.length > 0 && (
            <div className="absolute z-30 left-0 right-0 mt-2 bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden" data-testid="hero-search-suggestions">
              {suggestions.map((name) => (
                <button key={name} type="button" onMouseDown={() => { setQ(name); setShowSug(false); }}
                  className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-[#f0f6f3] flex items-center gap-2">
                  <GraduationCap size={15} className="text-[#004030]" /> {name}
                </button>
              ))}
            </div>
          )}
        </div>
        <Button data-testid="hero-search-btn" onClick={go} className="bg-[#004030] hover:bg-[#013d2b] text-white rounded-full h-11 px-6 font-semibold">Search</Button>
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        <select data-testid="hero-search-country" value={country} onChange={(e) => { setCountry(e.target.value); setUniversity(""); }} className={selCls}>
          <option value="">All countries</option>
          {(filters.countries || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="hero-search-university" value={university} onChange={(e) => setUniversity(e.target.value)} className={`${selCls} max-w-[220px]`}>
          <option value="">All universities</option>
          {uniGroups.map(([cn, us]) => (
            <optgroup key={cn} label={cn}>{us.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</optgroup>
          ))}
        </select>
      </div>
    </div>
  );
}

export default function Hero({ onSearch }) {
  return (
    <section className="relative bg-[#004030] overflow-hidden">
      {/* decorative glows */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#c9a227]/10 rounded-full blur-3xl -translate-y-1/3 translate-x-1/4" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#0a5540]/40 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-24 md:pt-20 md:pb-28 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-1.5 text-[#e0c05a] text-sm font-medium">
            <Star size={14} className="fill-[#e0c05a]" /> A trusted education group since 1996
          </span>
          <h1 className="font-display font-bold text-white text-4xl md:text-[58px] leading-[1.05] mt-6">
            Your Global Passport to <span className="gold-text">World-Class</span> Education
          </h1>
          <p className="mt-6 text-white/75 text-lg leading-relaxed max-w-xl">
            Expert counselling, university admissions, visa assistance and scholarship support — from Kuala Lumpur to campuses across the globe. Let {BRAND.name} build your roadmap.
          </p>

          {/* Course / university search — surfaced in the first screen */}
          <HeroSearch onSearch={onSearch} />

          <div className="flex flex-wrap gap-4 mt-6">
            <Link to="/contact">
              <Button className="bg-[#c9a227] hover:bg-[#e0c05a] text-[#013d2b] rounded-full h-13 px-7 py-6 text-base font-bold shadow-xl">
                Book a Free Consult <ArrowRight size={18} className="ml-1" />
              </Button>
            </Link>
            <Link to="/destinations">
              <Button variant="outline" className="rounded-full h-13 px-7 py-6 text-base font-semibold border-white/30 bg-white/5 text-white hover:bg-white hover:text-[#004030]">
                <PlayCircle size={18} className="mr-1" /> Explore Destinations
              </Button>
            </Link>
          </div>

          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-8">
            {["AI-Powered Program Match", "10+ Study Destinations", "Foreign Collaborations"].map((f) => (
              <span key={f} className="flex items-center gap-2 text-white/80 text-sm">
                <CheckCircle2 size={16} className="text-[#e0c05a]" /> {f}
              </span>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="relative rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white/10">
            <img src={HERO_IMG} alt="International students" className="w-full h-[440px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#012a1e]/40 to-transparent" />
          </div>
          {/* floating card */}
          <div className="absolute -bottom-6 -left-4 md:-left-8 bg-white rounded-2xl shadow-2xl p-4 flex items-center gap-3 animate-floaty">
            <div className="w-12 h-12 rounded-full bg-[#004030] grid place-items-center text-[#e0c05a] font-bold">
              30+
            </div>
            <div>
              <p className="text-sm font-bold text-[#012a1e]">Years of Expertise</p>
              <p className="text-xs text-slate-500">Guiding students since 1996</p>
            </div>
          </div>
          <div className="absolute -top-4 -right-2 md:-right-6 bg-[#c9a227] text-[#013d2b] rounded-2xl shadow-2xl px-5 py-3 animate-floaty" style={{ animationDelay: "1s" }}>
            <p className="font-display font-bold text-2xl">10+</p>
            <p className="text-xs font-semibold">Study Destinations</p>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="relative border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.label} className="text-center lg:text-left">
              <p className="font-display font-bold text-3xl md:text-4xl text-[#e0c05a]">
                <CountUp end={s.value} suffix={s.suffix} />
              </p>
              <p className="text-white font-semibold mt-1 text-sm md:text-base">{s.label}</p>
              <p className="text-white/55 text-xs mt-0.5">{s.sub}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
